import { useState } from "react";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Settings, RefreshCw, AlertCircle, CheckCircle, Clock, IdCard, Group } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ProviderConfigDialog } from "./ProviderConfigDialog";
import { IdentityProvider, ProviderType } from "@/types/identity-provider";
import { EmptyStateProviders } from "./EmptyStateProviders";

const mockProviders: IdentityProvider[] = [
  {
    id: "1",
    name: "Acme Corp Google Workspace",
    type: "google",
    status: "active",
    description: "Primary directory for all employees",
    lastSync: "2024-01-15T10:30:00Z",
    nextSync: "2024-01-16T10:30:00Z",
    userCount: 1245,
    groupCount: 24,
    config: {
      googleClientId: "client-123",
      googleDomain: "acmecorp.com"
    },
    syncSettings: {
      syncUsers: true,
      syncGroups: true,
      syncInterval: 24,
      enableAutoSync: true,
      attributeMapping: {
        email: "email",
        firstName: "given_name",
        lastName: "family_name",
        department: "department"
      }
    },
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-15T10:30:00Z"
  },
  {
    id: "2",
    name: "Azure AD Integration",
    type: "azure",
    status: "inactive",
    description: "Secondary directory for contractors",
    lastSync: "2024-01-10T14:20:00Z",
    userCount: 45,
    groupCount: 3,
    config: {
      azureTenantId: "tenant-456",
      azureClientId: "client-789"
    },
    syncSettings: {
      syncUsers: true,
      syncGroups: false,
      syncInterval: 12,
      enableAutoSync: false,
      attributeMapping: {
        email: "userPrincipalName",
        firstName: "givenName",
        lastName: "surname"
      }
    },
    createdAt: "2024-01-05T00:00:00Z",
    updatedAt: "2024-01-10T14:20:00Z"
  }
];

export function IdentityProvidersTab() {
  const [providers, setProviders] = useState<IdentityProvider[]>(mockProviders);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<IdentityProvider | null>(null);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'syncing':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'syncing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'error': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProviderIcon = (type: ProviderType) => {
    switch (type) {
      case 'google':
        return (
          <img 
            src="https://svgl.app/library/google.svg" 
            alt="Google" 
            className="w-8 h-8" 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      case 'azure':
        return (
          <img 
            src="https://svgl.app/library/azure.svg" 
            alt="Microsoft Azure" 
            className="w-8 h-8" 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      case 'ldap':
        return <div className="w-8 h-8 rounded bg-green-500 flex items-center justify-center text-white font-bold text-sm">LD</div>;
      case 'okta':
        return <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white font-bold text-sm">O</div>;
      case 'saml':
        return <div className="w-8 h-8 rounded bg-purple-500 flex items-center justify-center text-white font-bold text-sm">S</div>;
      default:
        return <div className="w-8 h-8 rounded bg-gray-500 flex items-center justify-center text-white font-bold text-sm">?</div>;
    }
  };

  const formatLastSync = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleAddProvider = () => {
    setEditingProvider(null);
    setConfigDialogOpen(true);
  };

  const handleEditProvider = (provider: IdentityProvider) => {
    setEditingProvider(provider);
    setConfigDialogOpen(true);
  };

  if (providers.length === 0) {
    return <EmptyStateProviders onAddProvider={handleAddProvider} />;
  }

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="space-y-1">
          <GlassCardTitle>Identity Providers</GlassCardTitle>
          <GlassCardDescription>
            Connect your directory services to sync users and groups
          </GlassCardDescription>
        </div>
        <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddProvider}>
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProvider ? 'Edit Identity Provider' : 'Add Identity Provider'}
              </DialogTitle>
            </DialogHeader>
            <ProviderConfigDialog
              provider={editingProvider}
              onSave={(provider) => {
                if (editingProvider) {
                  setProviders(providers.map(p => p.id === provider.id ? provider : p));
                } else {
                  setProviders([...providers, { ...provider, id: Date.now().toString() }]);
                }
                setConfigDialogOpen(false);
              }}
              onCancel={() => setConfigDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </GlassCardHeader>
      <GlassCardContent>
        <div className="grid gap-6 md:grid-cols-2">
        {providers.map((provider) => (
          <GlassCard key={provider.id} variant="default" hover={true}>
            <GlassCardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getProviderIcon(provider.type)}
                  <div>
                    <GlassCardTitle className="text-lg">{provider.name}</GlassCardTitle>
                    <GlassCardDescription className="mt-1">
                      {provider.description}
                    </GlassCardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={getStatusColor(provider.status)}>
                    {getStatusIcon(provider.status)}
                    <span className="ml-1 capitalize">{provider.status}</span>
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditProvider(provider)}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </GlassCardHeader>
            
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <IdCard className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Users:</span>
                  <span className="font-medium">{provider.userCount?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Group className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Groups:</span>
                  <span className="font-medium">{provider.groupCount || 0}</span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Last sync:</span>
                  <span className="ml-2">{formatLastSync(provider.lastSync)}</span>
                </div>
                {provider.nextSync && provider.status === 'active' && (
                  <div>
                    <span className="text-muted-foreground">Next sync:</span>
                    <span className="ml-2">{formatLastSync(provider.nextSync)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Now
                </Button>
                <Button variant="outline" size="sm" className="flex-1">
                  View Logs
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>
        ))}
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}